import * as THREE from 'three';
import { gsap } from 'gsap';

let renderer: THREE.WebGLRenderer | null = null;
let scene: THREE.Scene | null = null;
let camera: THREE.PerspectiveCamera | null = null;
let overlayPass: (() => void) | null = null;

// Touch phones get a lower cap: less fill + VRAM for the intro/fluid; SDF text stays crisp at 1.25×
let heroPass: (() => boolean) | null = null;
export function setHeroPass(pass: () => boolean): void { heroPass = pass; }
// Footer foil word: draws (and returns true) only while the footer is on screen.
let footerPass: (() => boolean) | null = null;
export function setFooterPass(pass: () => boolean): void { footerPass = pass; }

const MAX_DPR = window.matchMedia('(pointer: coarse)').matches ? 1.25 : 1.5;

// While set, the canvas is a foreground overlay and this pass replaces the particle render.
export function setOverlayPass(pass: (() => void) | null): void {
  overlayPass = pass;
}

export function initWebGL(): {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
} | null {
  const canvas = document.getElementById('webgl-canvas') as HTMLCanvasElement | null;
  if (!canvas) return null;

  scene = new THREE.Scene();

  camera = new THREE.PerspectiveCamera(
    45,
    window.innerWidth / window.innerHeight,
    0.1,
    100
  );
  camera.position.z = 5;

  try {
    renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: false,
      powerPreference: 'high-performance',
    });
  } catch {
    return null;   // no WebGL (disabled, blocklisted, lost): every module falls back to its DOM version
  }
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_DPR));

  window.addEventListener('resize', () => {
    if (!camera || !renderer) return;
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_DPR));
  });

  // Unified render loop synced to GSAP's ticker
  gsap.ticker.add(() => {
    if (!overlayPass && footerPass?.()) return; // the intro/fluid overlay always wins the frame
    if (overlayPass) {
      overlayPass();
      return;
    }
    if (heroPass?.()) return;
    if (renderer && scene && camera) {
      renderer.render(scene, camera);
    }
  });

  canvas.classList.add('active');

  return { scene, camera, renderer };
}

export function getWebGL() {
  return { scene, camera, renderer };
}

